import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaHeart,
  FaEnvelope,
  FaLock,
  FaGlobe,
  FaKey,
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaUser,
  FaShieldAlt,
  FaSun,
  FaMoon,
} from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";
import gsap from "gsap";
import api from "../services/api";
import toast, { Toaster } from "react-hot-toast";
const Login = () => {
  const [view, setView] = useState("login");
  const [loginMethod, setLoginMethod] = useState("password");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const [otpSent, setOtpSent] = useState(false);

  const [loadingSendOtp, setLoadingSendOtp] = useState(false);
  const [loadingLogin, setLoadingLogin] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);

  const [errors, setErrors] = useState({});
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return false; // Default to light mode
  });

  const { language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  React.useEffect(() => {
    // GSAP Entrance Animations
    const tl = gsap.timeline();
    tl.fromTo(
      ".gsap-branding",
      { opacity: 0, y: 30 },
      { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" }
    );
    tl.fromTo(
      ".gsap-form-card",
      { opacity: 0, x: 30 },
      { opacity: 1, x: 0, duration: 0.6, ease: "power3.out" },
      "-=0.4"
    );
    tl.fromTo(
      ".gsap-stagger-item",
      { opacity: 0, y: 15 },
      { opacity: 1, y: 0, duration: 0.5, stagger: 0.1, ease: "power2.out" },
      "-=0.2"
    );
  }, [view]);

  React.useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add("dark");
      document.documentElement.classList.remove("light");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.classList.remove("dark");
      document.documentElement.classList.add("light");
      localStorage.setItem("theme", "light");
    }
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const switchView = (newView) => {
    setView(newView);
    setOtpSent(false);
    setOtp("");
    setPassword("");
    setNewPassword("");
    setErrors({});
  };

  const handleSendOtp = async () => {
    if (!identifier.trim()) return;
    setLoadingSendOtp(true);
    setErrors({});
    try {
      // Determine if email or mobile
      const isEmail = identifier.includes("@");
      const body = {
        [isEmail ? "email" : "mobile"]: identifier,
        type: view === "login" ? "login" : "forgot", // crucial: type depends on current view
      };

      await api.post("/send-otp", body);
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP", {
        style: {
          borderRadius: "12px",
          background: "#fff",
          color: "#f43f5e",
          border: "1px solid #f43f5e",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#f43f5e",
          secondary: "#ffffff",
        },
      });
    } finally {
      setLoadingSendOtp(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoadingLogin(true);
    setErrors({});
    try {
      let response;
      if (loginMethod === "password") {
        const isEmail = identifier.includes("@");
        response = await api.post("/login", {
          [isEmail ? "email" : "mobile"]: identifier,
          password,
        });
      } else {
        // OTP Login → use verify-otp with type "login"
        const isEmail = identifier.includes("@");
        response = await api.post("/verify-otp", {
          [isEmail ? "email" : "mobile"]: identifier,
          otp,
          type: "login",
        });
      }

      const data = response.data;
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      toast.success("Login Successful! Welcome back ❤️", {
        style: {
          borderRadius: "12px",
          background: "linear-gradient(to right, #f43f5e, #ec4899)",
          color: "#fff",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#ffffff",
          secondary: "#f43f5e",
        },
      });

      setTimeout(() => {
        navigate("/dashboard", { replace: true });
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Login failed", {
        style: {
          borderRadius: "12px",
          background: "#fff",
          color: "#f43f5e",
          border: "1px solid #f43f5e",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#f43f5e",
          secondary: "#ffffff",
        },
      });
    } finally {
      setLoadingLogin(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoadingReset(true);
    setErrors({});
    try {
      const isEmail = identifier.includes("@");
      await api.post("/reset-password", {
        [isEmail ? "email" : "mobile"]: identifier,
        otp,
        newPassword,
      });
      switchView("login");
      setLoginMethod("password");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to reset password", {
        style: {
          borderRadius: "12px",
          background: "#fff",
          color: "#f43f5e",
          border: "1px solid #f43f5e",
          padding: "16px",
          fontWeight: "600",
        },
        iconTheme: {
          primary: "#f43f5e",
          secondary: "#ffffff",
        },
      });
    } finally {
      setLoadingReset(false);
    }
  };
  const t = {
    English: {
      welcome: "Welcome Back",
      pleaseLogin: "Please login to continue",
      emailOrMobile: "Email or Mobile Number",
      emailOnly: "Email",
      password: "Password",
      otp: "Enter OTP",
      newPassword: "New Password",
      login: "Login",
      sendOtp: "Send OTP",
      verifyLogin: "Verify & Login",
      resetPassword: "Reset Password",
      forgot: "Forgot Password?",
      loginWithOtp: "Or login with OTP",
      noAccount: "Don't have an account?",
      register: "Register Free",
      backToLogin: "Back to Login",
      resetTitle: "Reset Your Password",
      loading: "Loading...",
      tagline: "Where connections blossom",
    },
    Tamil: {
      welcome: "மீண்டும் வருக",
      pleaseLogin: "தொடர உள்நுழையவும்",
      emailOrMobile: "மின்னஞ்சல் அல்லது அலைபேசி எண்",
      emailOnly: "மின்னஞ்சல்",
      password: "கடவுச்சொல்",
      otp: "OTP ஐ உள்ளிடவும்",
      newPassword: "புதிய கடவுச்சொல்",
      login: "உள்நுழைய",
      sendOtp: "OTP அனுப்பு",
      verifyLogin: "சரிபார்த்து உள்நுழையவும்",
      resetPassword: "கடவுச்சொல்லை மீட்டமைக்கவும்",
      forgot: "கடவுச்சொல் மறந்ததா?",
      loginWithOtp: "அல்லது OTP மூலம் உள்நுழைய",
      noAccount: "கணக்கு இல்லையா?",
      register: "இலவச பதிவு",
      backToLogin: "உள்நுழைவுக்கு திரும்புக",
      resetTitle: "கடவுச்சொல்லை மீட்டமைக்கவும்",
      loading: "ஏற்றுகிறது...",
      tagline: "இணைப்புகள் மலரும் இடம்",
    },
  };

  const txt = t[language];

  const isIdentifierFilled = identifier.trim().length > 0;
  const isPasswordFilled = password.trim().length > 0;
  const isOtpFilled = otp.trim().length === 4;
  const isNewPasswordFilled = newPassword.trim().length >= 4;

  return (
    <div className="min-h-[100dvh] lg:h-screen bg-dark-navy relative overflow-x-hidden overflow-y-auto lg:overflow-hidden flex flex-col">
      {/* Background ambient glows */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-primary-pink/20 rounded-full blur-[120px]"></div>
        <div className="absolute bottom-[-10%] right-[-10%] w-96 h-96 bg-accent-cyan/20 rounded-full blur-[120px]"></div>
      </div>

      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
        }}
      />


      {/* Side-by-side Layout */}
      <div className="w-full h-full grid lg:grid-cols-2 grid-cols-1 z-10">
        {/* Left Side - Image & Branding */}
        <div className="hidden lg:flex flex-col justify-center items-center w-full h-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[rgba(255,46,136,0.15)] via-dark-navy to-dark-navy border-r border-glass-border shadow-[4px_0_24px_rgba(0,0,0,0.6)] text-white relative overflow-hidden">
          <div className="relative z-10 text-center px-12 gsap-branding">
            <div>

              <h1 className="text-4xl font-bold mb-3 tracking-wide text-transparent bg-clip-text bg-gradient-to-r from-text-primary to-text-secondary"><span className="text-primary-pink">Kalyana Matrimony</span></h1>
              <p className="text-xl font-medium text-text-secondary drop-shadow-sm mb-6">{txt.tagline}</p>
              <p className="text-sm text-text-muted max-w-md mx-auto">
                Find meaningful connections in a safe and trusted environment.
              </p>
            </div>
          </div>
          {/* Decorative hearts */}
          <div className="absolute bottom-10 left-10 text-primary-pink/60 text-9xl animate-float">
            <FaHeart />
          </div>
          <div className="absolute top-20 right-10 text-primary-pink/50 text-7xl rotate-12 animate-float" style={{ animationDelay: '1s' }}>
            <FaHeart />
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="flex flex-col w-full h-full overflow-y-auto p-4 lg:p-8 relative">
          <div
            className="w-full max-w-[90%] sm:max-w-lg md:max-w-xl lg:max-w-lg xl:max-w-2xl bg-glass-card backdrop-blur-[20px] border border-glass-border p-6 lg:p-8 rounded-[32px] shadow-glass relative gsap-form-card m-auto"
          >
            {/* Theme & Language Toggles */}
            <div className="flex justify-end gap-3 w-full mb-2 z-20 relative">
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 bg-[rgba(255,255,255,0.03)] backdrop-blur-md rounded-xl shadow-glass border border-glass-border hover:shadow-[0_0_15px_rgba(255,46,136,0.2)] transition text-text-primary"
                title="Toggle Theme"
              >
                {isDarkMode ? <FaSun className="text-lg text-premium-gold" /> : <FaMoon className="text-lg text-primary-pink" />}
              </button>
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[rgba(255,255,255,0.03)] backdrop-blur-md rounded-xl shadow-glass border border-glass-border hover:shadow-[0_0_15px_rgba(255,46,136,0.2)] transition text-text-primary text-sm sm:text-base"
              >
                <FaGlobe className="text-primary-pink" />
                <span className="font-medium">
                  {language === "English" ? "தமிழ்" : "English"}
                </span>
              </button>
            </div>

            <div className="gsap-stagger-item">
              {/* Mobile-only Branding */}
              <div className="lg:hidden text-center mb-3">
                <div className="p-3 bg-[rgba(255,46,136,0.1)] border border-[rgba(255,46,136,0.2)] rounded-full inline-block shadow-[0_0_15px_rgba(255,46,136,0.2)]">
                  <FaHeart className="text-4xl text-primary-pink" />
                </div>
                <h1 className="text-3xl font-bold text-text-primary mt-4 tracking-wide">
                  <span className="text-primary-pink">Kalyana Matrimony</span>
                </h1>
                <p className="text-text-secondary mt-1">{txt.tagline}</p>
              </div>

              <div className="text-center mb-4 gsap-stagger-item">
                <h2 className="text-3xl font-bold text-text-primary tracking-wide">
                  {view === "login" ? txt.welcome : txt.resetTitle}
                </h2>
                <p className="text-text-secondary mt-1">
                  {view === "login"
                    ? txt.pleaseLogin
                    : "Securely reset your password"}
                </p>
              </div>

              {view === "login" ? (
                <form onSubmit={handleLogin} className="space-y-3">
                  <div className="relative gsap-stagger-item">
                    <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                    <input
                      type="text"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-text-primary placeholder-text-muted shadow-inner"
                      placeholder={txt.emailOrMobile}
                      required
                    />
                    {errors.identifier && (
                      <p className="mt-2 text-sm text-primary-pink font-medium">
                        {errors.identifier}
                      </p>
                    )}
                  </div>

                  {loginMethod === "password" ? (
                    <div className="relative gsap-stagger-item">
                      <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full pl-12 pr-12 py-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-text-primary placeholder-text-muted shadow-inner"
                        placeholder={txt.password}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary transition-colors"
                      >
                        {showPassword ? <FaEyeSlash /> : <FaEye />}
                      </button>
                      {errors.password && (
                        <p className="mt-2 text-sm text-primary-pink font-medium">
                          {errors.password}
                        </p>
                      )}
                    </div>
                  ) : !otpSent ? null : (
                    <div className="relative gsap-stagger-item">
                      <FaKey className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.slice(0, 4))}
                        className="w-full pl-12 pr-4 py-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-gray-200 dark:border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-center tracking-widest text-lg text-text-primary dark:text-white placeholder-text-muted shadow-inner font-mono"
                        placeholder="••••"
                        maxLength="4"
                      />
                      {errors.otp && (
                        <p className="mt-2 text-sm text-primary-pink font-medium">
                          {errors.otp}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="space-y-3 gsap-stagger-item">
                    {loginMethod === "password" ? (
                      <>
                        <div className="text-right">
                          <button
                            type="button"
                            onClick={() => switchView("forgot-password")}
                            className="text-sm text-primary-pink hover:text-text-primary font-medium transition-colors"
                          >
                            {txt.forgot}
                          </button>
                        </div>
                        <div className="flex justify-center pt-2">
                          <button
                            type="submit"
                            disabled={
                              !isIdentifierFilled ||
                              !isPasswordFilled ||
                              loadingLogin
                            }
                            className="flex items-center justify-center gap-2 w-auto px-16 mx-auto py-2 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 text-lg"
                          >
                            {loadingLogin ? txt.loading : <><FaHeart className="animate-pulse" /> {txt.login}</>}
                          </button>
                        </div>
                      </>
                    ) : !otpSent ? (
                      <div className="flex justify-center pt-2">
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={!isIdentifierFilled || loadingSendOtp}
                          className="w-full py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 text-lg"
                        >
                          {loadingSendOtp ? txt.loading : txt.sendOtp}
                        </button>
                      </div>
                    ) : (
                      <div className="flex justify-center pt-2">
                        <button
                          type="submit"
                          disabled={!isOtpFilled || loadingLogin}
                          className="w-full py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 text-lg"
                        >
                          {loadingLogin ? txt.loading : txt.verifyLogin}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="text-center pt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setLoginMethod(
                          loginMethod === "password" ? "otp" : "password"
                        );
                        setOtpSent(false);
                        setErrors({});
                      }}
                      className="text-sm text-text-secondary hover:text-primary-pink transition-colors font-medium"
                    >
                      {loginMethod === "password"
                        ? txt.loginWithOtp
                        : "Use password instead"}
                    </button>
                  </div>
                </form>
              ) : (
                /* Forgot Password Form */
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <button
                    type="button"
                    onClick={() => switchView("login")}
                    className="flex items-center gap-2 text-text-muted hover:text-primary-pink text-sm font-medium transition-colors"
                  >
                    <FaArrowLeft /> {txt.backToLogin}
                  </button>

                  <div className="space-y-4">
                    <div className="relative">
                      <FaEnvelope className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                      <input
                        type="text"
                        value={identifier}
                        onChange={(e) => setIdentifier(e.target.value)}
                        className="w-full pl-12 pr-4 py-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-text-primary dark:text-white placeholder-text-muted bg-transparent dark:bg-[rgba(255,255,255,0.03)] border-gray-200 dark:border-glass-border shadow-inner"
                        placeholder={txt.emailOnly}
                        disabled={otpSent}
                        required
                      />
                      {errors.identifier && (
                        <p className="mt-2 text-sm text-primary-pink font-medium">
                          {errors.identifier}
                        </p>
                      )}
                    </div>

                    {!otpSent ? (
                      <div className="flex justify-center pt-2">
                        <button
                          type="button"
                          onClick={handleSendOtp}
                          disabled={!isIdentifierFilled || loadingSendOtp}
                          className="w-full py-3 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 text-lg"
                        >
                          {loadingSendOtp ? txt.loading : txt.sendOtp}
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="relative">
                          <FaKey className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                          <input
                            type="text"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value.slice(0, 6))}
                            className="w-full pl-12 pr-4 py-3 bg-transparent dark:bg-[rgba(255,255,255,0.03)] border border-gray-200 dark:border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-center tracking-widest text-lg text-text-primary dark:text-white placeholder-text-muted font-mono shadow-inner"
                            placeholder="••••"
                            maxLength="4"
                          />
                          {errors.otp && (
                            <p className="mt-2 text-sm text-primary-pink font-medium">
                              {errors.otp}
                            </p>
                          )}
                        </div>

                        <div className="relative">
                          <FaLock className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted" />
                          <input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full pl-12 pr-12 py-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl focus:border-primary-pink focus:ring-1 focus:ring-primary-pink outline-none transition text-text-primary dark:text-white placeholder-text-muted bg-transparent dark:bg-[rgba(255,255,255,0.03)] border-gray-200 dark:border-glass-border shadow-inner"
                            placeholder={txt.newPassword}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-white transition-colors"
                          >
                            {showNewPassword ? <FaEyeSlash /> : <FaEye />}
                          </button>
                          {errors.newPassword && (
                            <p className="mt-2 text-sm text-primary-pink font-medium">
                              {errors.newPassword}
                            </p>
                          )}
                        </div>

                        <div className="flex justify-center pt-2">
                          <button
                            type="submit"
                            disabled={
                              !isOtpFilled ||
                              !isNewPasswordFilled ||
                              loadingReset
                            }
                            className="w-full sm:w-auto py-3 px-10 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-50 disabled:hover:translate-y-0 text-lg"
                          >
                            {loadingReset ? txt.loading : txt.resetPassword}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </form>
              )}

              {/* Features & Register - Only show on login view */}
              {view === "login" && (
                <>
                  {/* Features */}
                  <div className="mt-4 grid grid-cols-2 gap-3 gsap-stagger-item">
                    <div className="flex items-center gap-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl p-4 transition hover:bg-[rgba(255,255,255,0.05)]">
                      <div className="p-2 bg-[rgba(255,46,136,0.1)] rounded-lg">
                        <FaUser className="text-primary-pink text-lg" />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary text-sm">Secure Login</p>
                        <p className="text-xs text-text-muted">Multiple methods</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 bg-[rgba(255,255,255,0.03)] border border-glass-border rounded-xl p-4 transition hover:bg-[rgba(255,255,255,0.05)]">
                      <div className="p-2 bg-[rgba(255,46,136,0.1)] rounded-lg">
                        <FaShieldAlt className="text-primary-pink text-lg" />
                      </div>
                      <div>
                        <p className="font-medium text-text-primary text-sm">Protected</p>
                        <p className="text-xs text-text-muted">
                          Data safety
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Register */}
                  <div className="mt-4 pt-4 border-t border-glass-border text-center gsap-stagger-item">
                    <p className="text-text-secondary text-sm mb-3">{txt.noAccount}</p>
                    <Link
                      to="/register"
                      className="inline-flex items-center justify-center gap-2 w-auto px-12 mx-auto py-2 bg-gradient-to-r from-primary-grad-start to-primary-grad-end text-white font-bold rounded-xl shadow-[0_0_20px_rgba(255,46,136,0.3)] hover:shadow-[0_0_25px_rgba(255,46,136,0.5)] hover:-translate-y-0.5 transition-all duration-300 backdrop-blur-md"
                    >
                      <FaHeart className="animate-pulse" /> {txt.register}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
